import PropTypes from 'prop-types';
import { Empty, Select } from 'antd';
import { Loading } from '@icp/components';
import { HELPER_TEXT_TYPES } from '@icp/form-renderer-core';
import { useEffect, useMemo, useRef } from 'react';
import { get } from 'lodash-es';
import FieldTitle from '../../FieldTitle';
import FormHelperText from '../../FormHelperText';
import ItemField from './ItemField';
import { filterOption } from './utils';

const loadingIndicator = 'lazy-loading-indicator';

function SelectAnt(props) {
  const {
    keyPath,
    id,
    title,
    value,
    componentProps,
    fieldTitleProps,
    validation,
    disabled,
    readonly,
    status,
    helpers,
    onChange,
    onTouchChanged,

    readonlyText,
    showSearch,
    searchTextColIds,
    loading,
    lazyLoading,
    moreLoading,
    useClientSideFilter,
    options,
    legacyMode,
    multiple,
    mapping,
    itemField,
    onSearch,
    onScroll,
    onClose,
  } = props;

  const divRef = useRef(null);

  const optionsWithLoading = useMemo(() => {
    if (lazyLoading && options?.length && moreLoading) {
      return [...options, { type: loadingIndicator, disabled: true }];
    }
    return options;
  }, [moreLoading, lazyLoading, options]);

  // acl 的 "已选中 1 项" 会在右侧占据更多的位置
  useEffect(() => {
    if (!multiple) return;

    const arrowDiv = divRef.current.querySelector('.ant-select-arrow');
    const selectorDiv = divRef.current.querySelector('.ant-select-selector');

    if (!arrowDiv || !selectorDiv) return;
    // 12 是 ant arrow down 的宽度，不会随着 size 属性变化
    if (arrowDiv.offsetWidth > 12) {
      selectorDiv.style.paddingRight += `${
        parseFloat(getComputedStyle(selectorDiv).paddingRight) + arrowDiv.offsetWidth - 12
      }px`;
    }
  }, [multiple, value]);

  const handleOpenChange = (open) => {
    if (!open && onClose) {
      onClose();
    }
  };

  const handleRightClick = (event) => {
    window.getSelection().selectAllChildren(event.target);
  };

  return (
    <>
      <FieldTitle required={validation?.required} {...fieldTitleProps}>
        {title}
      </FieldTitle>
      <div ref={divRef}>
        {readonly ? (
          <span className="readonly-text">{readonlyText}</span>
        ) : (
          <Select
            maxTagCount={10}
            popupMatchSelectWidth={false}
            {...componentProps}
            name={id}
            value={value ?? null}
            disabled={disabled}
            mode={legacyMode || (multiple ? 'multiple' : undefined)}
            status={status}
            showSearch={showSearch}
            options={optionsWithLoading}
            filterOption={
              useClientSideFilter
                ? (inputValue, option) => {
                    return filterOption(inputValue, option, searchTextColIds);
                  }
                : false
            }
            fieldNames={mapping}
            optionRender={(option) => {
              if (option.data.type === loadingIndicator) {
                return <Loading size={16} centered={false} delayed={false} />;
              }
              if (itemField) {
                return <ItemField keyPath={keyPath} itemField={itemField} option={option.data} />;
              }
              if (typeof option.label === 'string') {
                return option.label;
              }
              // mapping.label 可以是 a[0].b 的形式, antd 的 fieldNames 不支持
              return get(option.data, mapping.label);
            }}
            notFoundContent={
              loading || moreLoading ? (
                <Loading size={16} delayed={false} style={{ height: 56 }} />
              ) : (
                componentProps.notFoundContent || (
                  <Empty className="ant-empty-small" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              )
            }
            onChange={onChange}
            onSearch={onSearch}
            onBlur={onTouchChanged}
            onPopupScroll={onScroll}
            onDropdownVisibleChange={handleOpenChange}
            onContextMenu={handleRightClick}
          />
        )}
        <FormHelperText helpers={helpers} />
      </div>
    </>
  );
}

SelectAnt.propTypes = {
  keyPath: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
      }),
    ),
  ]),
  componentProps: PropTypes.shape({
    notFoundContent: PropTypes.node,
  }),
  legacyMode: PropTypes.string,
  multiple: PropTypes.bool,
  mapping: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    // Other key map to save
  }),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  showSearch: PropTypes.bool,
  searchTextColIds: PropTypes.arrayOf(PropTypes.string),
  lazyLoading: PropTypes.bool,
  itemField: PropTypes.shape({}),
  fieldTitleProps: PropTypes.shape({}),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
  readonlyText: PropTypes.string,
  loading: PropTypes.bool,
  moreLoading: PropTypes.bool,
  useClientSideFilter: PropTypes.bool,
  onSearch: PropTypes.func,
  onScroll: PropTypes.func,
  onClose: PropTypes.func,
};

export default SelectAnt;
